<template>
  <div>
    <div class="actions row col-lg-12 mb-5 p-0 m-0 justify-content-between">
      <div class="actions-left">
        <!-- Calendar view could be added here in the future -->
      </div>
      <div class="actions-right row col-lg-6 row justify-content-end p-0 m-0">
        <button type="button" class="btn btn-primary" @click="addSchedule" v-if="activeTab === 'active'">{{ $t('add') }}</button>
      </div>
    </div>
    
    <!-- Tabs -->
    <ul class="nav nav-tabs mb-3">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'active' }" @click="switchTab('active')" href="#">
          {{ $t('active') }}
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'archived' }" @click="switchTab('archived')" href="#">
          {{ $t('archived') }}
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'old' }" @click="switchTab('old')" href="#">
          {{ $t('old') }}
        </a>
      </li>
    </ul>
    
    <!-- List View -->
    <div>
      <div class="mb-3">
        <div class="filters-wrapper">
          <div class="form-group mr-2 mb-0">
            <label>{{ $t('schedules.date') }}</label>
            <input 
              type="date" 
              class="form-control" 
              v-model="searchDate"
              @change="onDateChanged"
              :placeholder="$t('schedules.date')"
            >
          </div>
          <button 
            class="btn btn-secondary btn-sm filter-clear-btn" 
            @click="clearDateFilter"
            v-if="searchDate"
          >
            {{ $t('clear') }}
          </button>
          <div class="form-group mr-2 mb-0 experience-filter-group">
            <label>{{ $t('schedules.experience') }}</label>
            <select
              class="form-control"
              v-model="selectedExperienceId"
              @change="onExperienceFilterChanged"
            >
              <option value="">{{ $t('all') }}</option>
              <option
                v-for="experience in experienceOptions"
                :key="experience.id"
                :value="experience.id"
              >
                {{ experience.name }}
              </option>
            </select>
          </div>
          <button
            class="btn btn-secondary btn-sm filter-clear-btn"
            @click="clearExperienceFilter"
            v-if="selectedExperienceId"
          >
            {{ $t('clear') }}
          </button>
        </div>
      </div>
      <CTableWrapper 
        ref="table" 
        hover 
        striped 
        :caption="$t('schedules')" 
        :items="items" 
        :actions="actions" 
        :pages="pages" 
        :fields="fields" 
        clickable-rows 
        @row-clicked="rowClicked" 
        @action-clicked="actionClicked" 
        @update-requested="updateItemsRequested" 
        :sorter="{external: true}" 
        :sorterValue="sorterValue" 
        @sorter-value-changed="sorterValueChanged" 
        :loading="loading"
      >
      </CTableWrapper>
    </div>
    <ConfirmDialog
      :title="confirmDialogTitle"
      :message="confirmDialogMessage"
      v-model="showConfirmDialog"
      @confirm="onConfirmArchive"
      @cancel="onCancelDialog"
    ></ConfirmDialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CTableWrapper from "@/controls/PaginatedTable.vue";
import ConfirmDialog from "@/controls/popups/ConfirmDialog.vue";
import router from '@/router';
import InputGetList from "@/classes/InputGetList";
import i18n from '@/helpers/i18n';
import { convertToPascalCase } from "@/helpers/utils.js";

export default {
  components: { CTableWrapper, ConfirmDialog },
  data () {
    return {
      activeTab: 'active',
      searchDate: '',
      selectedExperienceId: '',
      itemsPerPage: localStorage.itemsPerPage ?? 10,
      currentPage: 1,
      fields: [
        { key: 'experienceName', label: i18n.t('schedules.experience'), _classes: 'font-weight-bold', sortable: true },
        { key: 'guideName', label: i18n.t('schedules.guide'), sortable: true },
        { key: 'availableDate', label: i18n.t('schedules.date'), sortable: true },
        { key: 'startTime', label: i18n.t('schedules.startTime'), sortable: true },
        { key: 'minPeople', label: i18n.t('schedules.minPeople'), sortable: true },
        { key: 'maxPeople', label: i18n.t('schedules.maxPeople'), sortable: true },
        { key: 'capacity', label: i18n.t('schedules.capacity'), sortable: false },
        { key: 'isAvailable', label: i18n.t('schedules.isAvailable'), sortable: true },
        { key: 'source', label: i18n.t('schedules.source'), sortable: false },
      ],
      // Actions will be computed based on active tab
      sorterValue: { column: 'availableDate', asc: true },
      loading: false,
      showConfirmDialog: false,
      confirmDialogTitle: "",
      confirmDialogMessage: "",
      pendingArchiveItem: null,
      pendingAction: null // 'archive' or 'activate'
    }
  },
  computed: {
    actions() {
      const baseActions = [
        { key: 'view', label: i18n.t('view') }
      ];
      
      if (this.activeTab === 'archived') {
        baseActions.push({ key: 'activate', label: i18n.t('activate') });
      } else if (this.activeTab === 'old') {
        // Old schedules don't have archive/activate actions
      } else {
        baseActions.push({ key: 'archive', label: i18n.t('archive') });
      }
      
      return baseActions;
    },
    ...mapState('experienceAvailabilities', ['getExperienceAvailabilitiesOutput']),
    ...mapState('experiences', ['getExperiencesOutput']),
    ...mapState('experienceAvailabilities', ['deleteExperienceAvailabilityByIdOutput']),
    ...mapState('experienceAvailabilities', ['archiveExperienceAvailabilityByIdOutput']),
    ...mapState('experienceAvailabilities', ['unarchiveExperienceAvailabilityByIdOutput']),
    experienceIdToName() {
      const list = (this.getExperiencesOutput && this.getExperiencesOutput.data) ? this.getExperiencesOutput.data : [];
      const map = new Map();
      list.forEach(e => {
        if (e && e.id != null) {
          map.set(e.id, e.name || '');
        }
      });
      return map;
    },
    experienceAvailabilitiesInfo: function() {
      return this.getExperienceAvailabilitiesOutput ? this.getExperienceAvailabilitiesOutput : null;
    },
    experienceOptions() {
      const list = (this.getExperiencesOutput && this.getExperiencesOutput.data) ? this.getExperiencesOutput.data : [];
      return list;
    },
    items: function()
    {
      if(!(this.experienceAvailabilitiesInfo && this.experienceAvailabilitiesInfo.data)) return [];
      
      let items = this.experienceAvailabilitiesInfo.data.map(x => this.mapAvailabilityItem(x));

      // Filter old schedules if on "old" tab
      if (this.activeTab === 'old') {
        const now = new Date();
        items = items.filter(item => {
          if (!item.availableDate || !item.startTime) return false;
          
          // Parse the date and time
          let scheduleDate;
          if (item.availableDate instanceof Date) {
            scheduleDate = new Date(item.availableDate);
          } else if (typeof item.availableDate === 'string') {
            // Handle ISO string or date string
            scheduleDate = new Date(item.availableDate);
          } else {
            return false;
          }
          
          // Extract time components
          let timeParts = item.startTime.split(':');
          if (timeParts.length < 2) return false;
          
          const hours = parseInt(timeParts[0], 10);
          const minutes = parseInt(timeParts[1], 10);
          
          // Create full datetime
          scheduleDate.setHours(hours, minutes, 0, 0);
          
          // Check if schedule datetime is in the past
          return scheduleDate < now;
        });
        
        // Paginate in frontend for "old" tab
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        items = items.slice(startIndex, endIndex);
      }
      
      return items;
    },
    filteredItemsForOldTab() {
      // Get all filtered items (before pagination) for "old" tab
      if (this.activeTab !== 'old' || !(this.experienceAvailabilitiesInfo && this.experienceAvailabilitiesInfo.data)) {
        return [];
      }
      
      let items = this.experienceAvailabilitiesInfo.data.map(x => this.mapAvailabilityItem(x));

      const now = new Date();
      return items.filter(item => {
        if (!item.availableDate || !item.startTime) return false;
        
        let scheduleDate;
        if (item.availableDate instanceof Date) {
          scheduleDate = new Date(item.availableDate);
        } else if (typeof item.availableDate === 'string') {
          scheduleDate = new Date(item.availableDate);
        } else {
          return false;
        }
        
        let timeParts = item.startTime.split(':');
        if (timeParts.length < 2) return false;
        
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        
        scheduleDate.setHours(hours, minutes, 0, 0);
        
        return scheduleDate < now;
      });
    },
    pages: function()
    {
      // If on "old" tab, calculate pages based on all filtered items (before pagination)
      if (this.activeTab === 'old') {
        const totalFilteredItems = this.filteredItemsForOldTab.length;
        if (totalFilteredItems === 0) return 1;
        return Math.ceil(totalFilteredItems / this.itemsPerPage);
      }
      // Otherwise use the API pagination
      return this.experienceAvailabilitiesInfo ? this.experienceAvailabilitiesInfo.totalPages : 1;
    }
  },
  mounted: function()
  {
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  activated: function()
  {
    // Refresh the list when the component is activated (e.g., returning from add/edit page)
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  methods: {
    ...mapActions('experienceAvailabilities', ['getExperienceAvailabilities']),
    ...mapActions('experiences', ['getExperiences']),
    ...mapActions('experienceAvailabilities', ['deleteExperienceAvailabilityById']),
    ...mapActions('experienceAvailabilities', ['archiveExperienceAvailabilityById']),
    ...mapActions('experienceAvailabilities', ['unarchiveExperienceAvailabilityById']),
    rowClicked (item, index) {
      this.$router.push({ name: 'ExperienceAvailabilitiesEdit', query: { id: item.id } });
    },
    actionClicked(item, action){
      switch(action.key)
      {
        case 'view':
        {
          this.editSchedule(item);
          break;
        }
        case 'archive':
        {
          this.pendingArchiveItem = item;
          this.pendingAction = 'archive';
          this.confirmDialogTitle = this.$t('archive');
          // Use "horário" (singular) instead of "horários" (plural)
          const scheduleText = this.$i18n.locale === 'pt' ? 'horário' : 'schedule';
          this.confirmDialogMessage = this.$i18n.t('archive') + ' ' + scheduleText + '?';
          this.showConfirmDialog = true;
          break;
        }
        case 'activate':
        {
          this.pendingArchiveItem = item;
          this.pendingAction = 'activate';
          this.confirmDialogTitle = this.$t('activate');
          // Use "horário" (singular) instead of "horários" (plural)
          const scheduleText = this.$i18n.locale === 'pt' ? 'horário' : 'schedule';
          this.confirmDialogMessage = this.$i18n.t('activate') + ' ' + scheduleText + '?';
          this.showConfirmDialog = true;
          break;
        }
        default:
        {
          throw "The provided action is not supported!"; 
        }
      }
    },
    async updateItemsRequested(currentPage, itemsPerPage)
    {
      this.loading = true;
      this.itemsPerPage = itemsPerPage;
      this.currentPage = currentPage; // Store current page for frontend pagination

      // For "old" tab, fetch a large number of items to filter in frontend
      const pageSize = this.activeTab === 'old' ? 1000 : itemsPerPage;
      const pageNumber = this.activeTab === 'old' ? 1 : currentPage;

      var input = new InputGetList(pageSize, pageNumber, '', this.sorterValue.asc ? 1 : 2, convertToPascalCase(this.sorterValue.column));
      
      // Add date parameter if provided
      if (this.searchDate) {
        input.date = this.searchDate;
      }
      if (this.selectedExperienceId) {
        const experienceId = parseInt(this.selectedExperienceId, 10);
        if (!isNaN(experienceId)) {
          input.experienceId = experienceId;
        }
      }
      
      // Add isArchived parameter based on active tab
      // For "old" tab, we don't filter by isArchived - we'll filter by date in the frontend
      if (this.activeTab !== 'old') {
        input.isArchived = this.activeTab === 'archived';
      }
      
      await this.ensureExperiencesLoaded();

      this.getExperienceAvailabilities(input); 
    },
    switchTab(tab) {
      if (this.activeTab !== tab) {
        this.activeTab = tab;
        this.$refs.table.reset();
        // The activeTab watcher will handle the list update
      }
    },
    addSchedule () 
    {
      router.push({ name: 'ExperienceAvailabilitiesAdd'});
    },
    editSchedule(schedule) {
      this.$router.push({ name: 'ExperienceAvailabilitiesEdit', query: { id: schedule.id } });
    },
    sorterValueChanged(value)
    {
      this.sorterValue = value;
    },
    onDateChanged() {
      this.$refs.table.reset();
      this.updateItemsRequested(1, this.itemsPerPage);
    },
    clearDateFilter() {
      this.searchDate = '';
      this.$refs.table.reset();
      this.updateItemsRequested(1, this.itemsPerPage);
    },
    onExperienceFilterChanged() {
      this.$refs.table.reset();
      this.updateItemsRequested(1, this.itemsPerPage);
    },
    clearExperienceFilter() {
      this.selectedExperienceId = '';
      this.$refs.table.reset();
      this.updateItemsRequested(1, this.itemsPerPage);
    },
    async ensureExperiencesLoaded() {
      if (!this.getExperiencesOutput || !Array.isArray(this.getExperiencesOutput.data) || this.getExperiencesOutput.data.length === 0) {
        const experiencesInput = new InputGetList(200, 1, '', 1, 'Position');
        try {
          await this.getExperiences(experiencesInput);
        } catch (error) {
          console.error('Error loading experiences:', error);
        }
      }
    },
    onConfirmArchive() {
      if (this.pendingArchiveItem && this.pendingAction) {
        if (this.pendingAction === 'archive') {
          this.archiveExperienceAvailabilityById(this.pendingArchiveItem.id);
        } else if (this.pendingAction === 'activate') {
          this.unarchiveExperienceAvailabilityById(this.pendingArchiveItem.id);
        }
        this.pendingArchiveItem = null;
        this.pendingAction = null;
      }
      this.showConfirmDialog = false;
    },
    onCancelDialog() {
      this.pendingArchiveItem = null;
      this.pendingAction = null;
      this.showConfirmDialog = false;
    },
    mapAvailabilityItem(x) {
      const bookedPeople = Math.max(0, x.maxPeople - (x.availableSpots ?? x.maxPeople));
      return {
        ...x,
        experienceName: (x.experience && x.experience.name) ? x.experience.name : (this.experienceIdToName.get(x.experienceId) || `#${x.experienceId}`),
        guideName: x.guide && x.guide.name ? x.guide.name : '',
        source: x.availabilityIntervalId ? i18n.t('schedules.fromInterval') : i18n.t('schedules.manual'),
        capacity: `${bookedPeople}/${x.maxPeople}`
      };
    }
  },
  watch:
  {
    sorterValue:
    {
      handler: function(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      },
      deep: true
    },
    deleteExperienceAvailabilityByIdOutput: function(val)
    {
      this.loading = true;

      if(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    archiveExperienceAvailabilityByIdOutput: function(val)
    {
      if(val)
      {
        this.loading = true;
        // Update the current list after archiving
        // If on active tab, this will refresh active list (removing archived item)
        // If on archived tab, this will refresh archived list (showing newly archived item)
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    unarchiveExperienceAvailabilityByIdOutput: function(val)
    {
      if(val)
      {
        this.loading = true;
        // Update the current list after activating/unarchiving
        // If on archived tab, this will refresh archived list (removing activated item)
        // If on active tab, this will refresh active list (showing newly activated item)
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    activeTab: function(newTab, oldTab)
    {
      // When switching tabs, always refresh the list to ensure we have the latest data
      // This ensures that when switching to archived tab, we see the most recent archived items
      // The oldTab !== undefined check prevents refresh on initial mount
      if (newTab !== oldTab && oldTab !== undefined) {
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    getExperienceAvailabilitiesOutput: function(val)
    {
      if(val != null)
      {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.actions-left {
  display: flex;
  align-items: center;
}

.btn-group .btn {
  margin-right: 0;
}

.nav-tabs .nav-link {
  cursor: pointer;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
}

.filters-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.filters-wrapper .btn {
  height: 38px;
  margin-bottom: 0;
}
.filter-clear-btn {
  white-space: nowrap;
}
.experience-filter-group {
  min-width: 220px;
  max-width: 320px;
  flex: 0 0 auto;
}
</style>

